import type { AudioAssetPickerSelection } from '../../../components/AudioAssetPicker/audioAssetPickerContract'
import { classifyPersistedGenerateAudioError } from './generateAudioError'

/** VistoeraTASK-00217 — Provider Capability defaults used ONLY as the
 * last-resort fallback (precedence 3) when the newly-bound Voice
 * Template has no remembered Pitch/Speed of its own (precedence 2). */
export interface AudioGenerationParameterCapabilityDefaults {
  pitch: number | null
  speed: number | null
}

/** Atomic field patch when Generate Audio binds an existing library Voice Template. */
export function buildGenerateAudioLibraryVoiceTemplateReplacementFields(args: {
  currentFields: Record<string, unknown>
  selection: Pick<AudioAssetPickerSelection, 'voiceTemplateId' | 'name' | 'usable' | 'generationPreference'>
  capabilityDefaults?: AudioGenerationParameterCapabilityDefaults | null
}): Record<string, unknown> | null {
  const voiceTemplateId = (args.selection.voiceTemplateId || '').trim()
  if (!voiceTemplateId || !args.selection.usable) return null

  const fields = args.currentFields
  const hadCompletedResult = fields.status === 'COMPLETED' && Boolean(fields.audioAssetId)
  const templateChanged = fields.selectedVoiceTemplateId !== voiceTemplateId
  const voiceBindingFailure = classifyPersistedGenerateAudioError({
    message: fields.errorMessage as string | null | undefined,
    errorCode: fields.errorCode as string | null | undefined,
  }).voiceBindingFailure

  const next: Record<string, unknown> = {
    selectedVoiceTemplateId: voiceTemplateId,
    selectedSystemVoiceKey: null,
    selectedVoiceLabel: args.selection.name,
    selectedVoiceTemplateExecutable: true,
    pendingVoiceTemplateCandidateNodeId: null,
  }

  if (voiceBindingFailure) {
    next.errorMessage = null
    next.errorCode = null
    if (fields.status === 'FAILED') {
      next.status = 'READY'
    }
  }

  if (hadCompletedResult && templateChanged) {
    next.needsRegeneration = true
    next.previousAudioAssetId = fields.audioAssetId
  }

  // VistoeraTASK-00217 — Voice Template Parameter Memory (spec §11-§13).
  // Only apply on a REAL switch to a DIFFERENT template — rebinding the
  // SAME template (e.g. a redundant confirm click) must leave whatever
  // the node currently has untouched (precedence 1: "node's own
  // currently-saved params" wins when there was no actual switch).
  // Precedence when switching: (2) the target template's own remembered
  // value, else (3) the active Provider Capability's own default.
  if (templateChanged) {
    const pref = args.selection.generationPreference
    const pitch = pref?.pitch ?? args.capabilityDefaults?.pitch ?? null
    const speed = pref?.speed ?? args.capabilityDefaults?.speed ?? null
    next.pitch = pitch
    next.speed = speed
  }

  return next
}
